import axios from "axios";
import { useEffect, useRef, useState } from "react";
import "./styles/Purchase.css";

function Purchase() {
  const [purchases, setPurchases] = useState([]);
  const [filterDate, setFilterDate] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });
  
  // Product states
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [productSearch, setProductSearch] = useState("");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const productInputRef = useRef(null);
  const productDropdownRef = useRef(null);
  
  // Vendor states
  const [vendors, setVendors] = useState([]);
  const [filteredVendors, setFilteredVendors] = useState([]);
  const [showVendorDropdown, setShowVendorDropdown] = useState(false);
  const [vendorSearch, setVendorSearch] = useState("");
  const [selectedVendor, setSelectedVendor] = useState(null);
  const vendorInputRef = useRef(null);
  const vendorDropdownRef = useRef(null);
  
  // Modal states
  const [showProductModal, setShowProductModal] = useState(false);
  const [showVendorModal, setShowVendorModal] = useState(false);
  const [showProductList, setShowProductList] = useState(false);
  const [showVendorList, setShowVendorList] = useState(false);
  const [vendorList, setVendorList] = useState([]);
  const [productList, setProductList] = useState([]);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editingVendor, setEditingVendor] = useState(null);
  const [savingItem, setSavingItem] = useState(false);
  
  // Product form - NO recommended_price
  const [productForm, setProductForm] = useState({
    category: "",
    item_name: "",
    base_unit: "PCS",
    purchase_packaging: "",
    packaging_quantity: 1,
    vendor_id: "",
    vendor_name: ""
  });
  
  // Vendor form
  const [vendorForm, setVendorForm] = useState({
    name: "",
    phone: "",
    payment_methods: "",
    delivery_type: "",
    id_number: "",
    category: ""
  });
  
  // Purchase form
  const [formData, setFormData] = useState({
    category: "",
    product_id: "",
    product_name: "",
    vendor_id: "",
    vendor_name: "",
    quantity: "",
    packaging_type: "",
    packaging_quantity: 1,
    unit: "",
    price_per_pack: "",
    price_per_unit: "",
    total_amount: ""
  });

  const getToken = () => localStorage.getItem("token") || "";
  const staff = JSON.parse(localStorage.getItem("staff") || "{}");
  const isAdmin = staff.role === "Admin";

  // Vendor categories list
  const vendorCategories = [
    "PACKAGING",
    "FRUITS & VEGGY",
    "BUTCHERY",
    "GROCERIES",
    "MILK AND CHEESE",
    "SPICES",
    "SOFT DRINKS",
    "COFFEE",
    "STATIONERY",
    "BEERS, LIQUORS",
    "DRINKS",
    "EGGS",
    "SERVICE & REPAIR",
    "KITCHENWARE",
    "SALES & MARKETING"
  ];

  // Product categories list
  const productCategories = [
    "PACKS",
    "FRUITS & VEGETABLES",
    "GROCERY",
    "MEAT & CHICKEN",
    "MILK",
    "SAUCE",
    "SPICES",
    "SOFT DRINKS",
    "BEERS",
    "LIQUORS",
    "OTHER"
  ];

  // Helper function to safely format numbers
  const safeNumber = (value) => {
    if (value === null || value === undefined || value === "") return 0;
    const num = parseFloat(value);
    return isNaN(num) ? 0 : num;
  };

  // Helper function to safely format to fixed decimal
  const safeToFixed = (value, decimals = 2) => {
    const num = safeNumber(value);
    return num.toFixed(decimals);
  };

  // Helper function to safely format locale string
  const safeToLocaleString = (value) => {
    const num = safeNumber(value);
    return num.toLocaleString();
  };

  // Fetch products
  const fetchProducts = async (search = "") => {
    try {
      const token = getToken();
      let url = `https://okna.co.tz/mrms/Products.php?token=${token}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      const response = await axios.get(url);
      if (!response.data.error) {
        setProducts(response.data);
        setProductList(response.data);
        if (search) {
          const filtered = response.data.filter(p => 
            p.item_name.toLowerCase().includes(search.toLowerCase()) ||
            p.category.toLowerCase().includes(search.toLowerCase()) ||
            (p.vendor_name && p.vendor_name.toLowerCase().includes(search.toLowerCase()))
          );
          setFilteredProducts(filtered);
        } else {
          setFilteredProducts(response.data);
        }
      }
    } catch (error) {
      console.error("Error fetching products:", error);
    }
  };

  // Fetch vendors
  const fetchVendors = async (search = "") => {
    try {
      const token = getToken();
      let url = `https://okna.co.tz/mrms/Vendor.php?token=${token}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      const response = await axios.get(url);
      if (!response.data.error) {
        setVendors(response.data);
        setVendorList(response.data);
        if (search) {
          const filtered = response.data.filter(v => 
            v.name.toLowerCase().includes(search.toLowerCase()) ||
            (v.phone && v.phone.includes(search)) ||
            (v.category && v.category.toLowerCase().includes(search.toLowerCase()))
          );
          setFilteredVendors(filtered);
        } else {
          setFilteredVendors(response.data);
        }
      }
    } catch (error) {
      console.error("Error fetching vendors:", error);
    }
  };

  // Fetch purchases
  const fetchPurchases = async () => {
    setLoading(true);
    try {
      const token = getToken();
      let url = "https://okna.co.tz/mrms/Purchase.php?token=" + token;
      if (filterDate) url += "&date=" + filterDate;
      
      const response = await axios.get(url);
      if (response.data.error) {
        setMessage({ text: response.data.error, type: "error" });
      } else {
        setPurchases(response.data);
      }
    } catch (error) {
      setMessage({ text: "Error fetching purchases", type: "error" });
    } finally {
      setLoading(false);
    }
  };

  // Save product - NO recommended_price
  const saveProduct = async () => {
    if (!productForm.item_name || !productForm.category) {
      setMessage({ text: "Product name and category required", type: "error" });
      return;
    }

    setSavingItem(true);
    try {
      const token = getToken();
      const url = `https://okna.co.tz/mrms/Products.php?token=${token}`;
      const method = editingProduct ? "PUT" : "POST";
      
      const data = {
        category: productForm.category,
        item_name: productForm.item_name,
        base_unit: productForm.base_unit,
        purchase_packaging: productForm.purchase_packaging,
        packaging_quantity: productForm.packaging_quantity,
        vendor_id: productForm.vendor_id === "" ? null : productForm.vendor_id,
        vendor_name: productForm.vendor_name
      };
      
      if (editingProduct) {
        data.id = editingProduct.id;
      }
      
      const response = await axios({ method, url, data });
      
      if (response.data.error) {
        setMessage({ text: response.data.error, type: "error" });
      } else {
        setMessage({ text: response.data.message, type: "success" });
        resetProductForm();
        fetchProducts();
        setTimeout(() => setMessage({ text: "", type: "" }), 3000);
      }
    } catch (error) {
      console.error("Save product error:", error);
      setMessage({ text: "Error saving product", type: "error" });
    } finally {
      setSavingItem(false);
    }
  };

  // Save vendor
  const saveVendor = async () => {
    if (!vendorForm.name) {
      setMessage({ text: "Vendor name required", type: "error" });
      return;
    }

    setSavingItem(true);
    try {
      const token = getToken();
      const url = `https://okna.co.tz/mrms/Vendor.php?token=${token}`;
      const method = editingVendor ? "PUT" : "POST";
      const data = editingVendor ? { ...vendorForm, id: editingVendor.id } : vendorForm;
      
      const response = await axios({ method, url, data });
      
      if (response.data.error) {
        setMessage({ text: response.data.error, type: "error" });
      } else {
        setMessage({ text: response.data.message, type: "success" });
        resetVendorForm();
        fetchVendors();
        setTimeout(() => setMessage({ text: "", type: "" }), 3000);
      }
    } catch (error) {
      console.error("Save vendor error:", error);
      setMessage({ text: "Error saving vendor", type: "error" });
    } finally {
      setSavingItem(false);
    }
  };

  // Delete product
  const deleteProduct = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try {
      const token = getToken();
      const response = await axios.delete(`https://okna.co.tz/mrms/Products.php?id=${id}&token=${token}`);
      if (response.data.error) {
        setMessage({ text: response.data.error, type: "error" });
      } else {
        setMessage({ text: response.data.message, type: "success" });
        fetchProducts();
        setTimeout(() => setMessage({ text: "", type: "" }), 3000);
      }
    } catch (error) {
      setMessage({ text: "Error deleting product", type: "error" });
    }
  };

  // Delete vendor
  const deleteVendor = async (id) => {
    if (!window.confirm("Delete this vendor?")) return;
    try {
      const token = getToken();
      const response = await axios.delete(`https://okna.co.tz/mrms/Vendor.php?id=${id}&token=${token}`);
      if (response.data.error) {
        setMessage({ text: response.data.error, type: "error" });
      } else {
        setMessage({ text: response.data.message, type: "success" });
        fetchVendors();
        setTimeout(() => setMessage({ text: "", type: "" }), 3000);
      }
    } catch (error) {
      setMessage({ text: "Error deleting vendor", type: "error" });
    }
  };

  // Edit product - NO recommended_price
  const editProduct = (product) => {
    setEditingProduct(product);
    setProductForm({
      category: product.category || "",
      item_name: product.item_name || "",
      base_unit: product.base_unit || "PCS",
      purchase_packaging: product.purchase_packaging || "",
      packaging_quantity: product.packaging_quantity || 1,
      vendor_id: product.vendor_id || "",
      vendor_name: product.vendor_name || ""
    });
    setShowProductModal(true);
    setShowProductList(false);
  };

  // Edit vendor
  const editVendor = (vendor) => {
    setEditingVendor(vendor);
    setVendorForm({
      name: vendor.name || "",
      phone: vendor.phone || "",
      payment_methods: vendor.payment_methods || "",
      delivery_type: vendor.delivery_type || "",
      id_number: vendor.id_number || "",
      category: vendor.category || ""
    });
    setShowVendorModal(true);
    setShowVendorList(false);
  };

  // Select product
  const selectProduct = (product) => {
    setSelectedProduct(product);
    setProductSearch(product.item_name);
    setShowProductDropdown(false);
    
    setFormData({
      ...formData,
      product_id: product.id,
      product_name: product.item_name,
      category: product.category,
      unit: product.base_unit,
      packaging_type: product.purchase_packaging || "",
      packaging_quantity: product.packaging_quantity || 1,
      price_per_pack: "",
      price_per_unit: "",
      total_amount: ""
    });
    
    if (product.vendor_name) {
      setVendorSearch(product.vendor_name);
      setSelectedVendor({ name: product.vendor_name, id: product.vendor_id });
      setFormData(prev => ({ ...prev, vendor_id: product.vendor_id, vendor_name: product.vendor_name }));
    }
  };

  // Select vendor
  const selectVendor = (vendor) => {
    setSelectedVendor(vendor);
    setVendorSearch(vendor.name);
    setShowVendorDropdown(false);
    setFormData(prev => ({ ...prev, vendor_id: vendor.id, vendor_name: vendor.name }));
  };

  // Edit purchase
  const editPurchase = (purchase) => {
    const quantity = safeNumber(purchase.quantity);
    const pricePerPack = safeNumber(purchase.price_per_pack || purchase.price);
    const pricePerUnit = safeNumber(purchase.price_per_unit);
    const totalAmount = safeNumber(purchase.total_amount || purchase.amount);
    const packagingQty = safeNumber(purchase.packaging_quantity) || 1;
    
    setFormData({
      category: purchase.category || "",
      product_id: purchase.product_id || "",
      product_name: purchase.product_name || purchase.item_name || "",
      vendor_id: purchase.vendor_id || "",
      vendor_name: purchase.vendor_name || purchase.vendor || "",
      quantity: quantity,
      packaging_type: purchase.packaging_type || purchase.unit || "",
      packaging_quantity: packagingQty,
      unit: purchase.unit || "",
      price_per_pack: pricePerPack,
      price_per_unit: pricePerUnit,
      total_amount: totalAmount
    });
    
    setProductSearch(purchase.product_name || purchase.item_name || "");
    setVendorSearch(purchase.vendor_name || purchase.vendor || "");
    
    if (purchase.product_id) {
      setSelectedProduct({ id: purchase.product_id, item_name: purchase.product_name || purchase.item_name });
    }
    
    if (purchase.vendor_id) {
      setSelectedVendor({ id: purchase.vendor_id, name: purchase.vendor_name || purchase.vendor });
    }
    
    setEditingId(purchase.id);
    setShowForm(true);
  };

  // Delete purchase
  const deletePurchase = async (id) => {
    if (!window.confirm("Delete this purchase?")) return;
    setLoading(true);
    try {
      const token = getToken();
      const response = await axios.delete(`https://okna.co.tz/mrms/Purchase.php?id=${id}&token=${token}`);
      if (response.data.error) {
        setMessage({ text: response.data.error, type: "error" });
      } else {
        setMessage({ text: response.data.message, type: "success" });
        fetchPurchases();
        setTimeout(() => setMessage({ text: "", type: "" }), 3000);
      }
    } catch (error) {
      setMessage({ text: "Error deleting purchase", type: "error" });
    } finally {
      setLoading(false);
    }
  };

  // Calculate prices
  const calculatePrices = (qty, packagingQty, packPrice) => {
    const totalUnits = qty * (packagingQty || 1);
    const pricePerUnit = totalUnits > 0 ? packPrice / totalUnits : 0;
    return { totalUnits, pricePerUnit };
  };

  // Handle purchase quantity change
  const handleQuantityChange = (e) => {
    const qty = parseFloat(e.target.value) || 0;
    const packPrice = parseFloat(formData.price_per_pack) || 0;
    const packagingQty = parseFloat(formData.packaging_quantity) || 1;
    
    const { pricePerUnit } = calculatePrices(qty, packagingQty, packPrice);
    
    setFormData({
      ...formData,
      quantity: qty,
      total_amount: packPrice * qty,
      price_per_unit: pricePerUnit
    });
  };

  // Handle pack price change
  const handlePackPriceChange = (e) => {
    const packPrice = parseFloat(e.target.value) || 0;
    const qty = parseFloat(formData.quantity) || 0;
    const packagingQty = parseFloat(formData.packaging_quantity) || 1;
    
    const { pricePerUnit } = calculatePrices(qty, packagingQty, packPrice);
    
    setFormData({
      ...formData,
      price_per_pack: packPrice,
      total_amount: packPrice * qty,
      price_per_unit: pricePerUnit
    });
  };

  // Reset forms
  const resetProductForm = () => {
    setProductForm({
      category: "",
      item_name: "",
      base_unit: "PCS",
      purchase_packaging: "",
      packaging_quantity: 1,
      vendor_id: "",
      vendor_name: ""
    });
    setEditingProduct(null);
    setShowProductModal(false);
  };

  const resetVendorForm = () => {
    setVendorForm({
      name: "",
      phone: "",
      payment_methods: "",
      delivery_type: "",
      id_number: "",
      category: ""
    });
    setEditingVendor(null);
    setShowVendorModal(false);
  };

  const resetForm = () => {
    setFormData({
      category: "",
      product_id: "",
      product_name: "",
      vendor_id: "",
      vendor_name: "",
      quantity: "",
      packaging_type: "",
      packaging_quantity: 1,
      unit: "",
      price_per_pack: "",
      price_per_unit: "",
      total_amount: ""
    });
    setSelectedProduct(null);
    setSelectedVendor(null);
    setProductSearch("");
    setVendorSearch("");
    setEditingId(null);
    setShowForm(false);
  };

  // Save purchase
  const savePurchase = async () => {
    if (!formData.product_id || !formData.vendor_id || !formData.quantity || !formData.price_per_pack) {
      setMessage({ text: "Please fill all required fields", type: "error" });
      return;
    }

    setLoading(true);
    try {
      const token = getToken();
      const saveData = {
        product_id: formData.product_id,
        product_name: formData.product_name,
        category: formData.category,
        vendor_id: formData.vendor_id,
        vendor_name: formData.vendor_name,
        quantity: safeNumber(formData.quantity),
        packaging_type: formData.packaging_type,
        packaging_quantity: safeNumber(formData.packaging_quantity),
        unit: formData.unit,
        price_per_pack: safeNumber(formData.price_per_pack),
        price_per_unit: safeNumber(formData.price_per_unit),
        total_amount: safeNumber(formData.total_amount)
      };
      
      let response;
      if (editingId) {
        saveData.id = editingId;
        response = await axios.put(`https://okna.co.tz/mrms/Purchase.php?token=${token}`, saveData);
      } else {
        response = await axios.post(`https://okna.co.tz/mrms/Purchase.php?token=${token}`, saveData);
      }
      
      if (response.data.error) {
        setMessage({ text: response.data.error, type: "error" });
      } else {
        setMessage({ text: response.data.message, type: "success" });
        resetForm();
        fetchPurchases();
        setTimeout(() => setMessage({ text: "", type: "" }), 3000);
      }
    } catch (error) {
      console.error("Save purchase error:", error);
      setMessage({ text: "Error saving purchase", type: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPurchases();
    fetchProducts();
    fetchVendors();
  }, [filterDate]);

  // Handle product search
  const handleProductSearch = (e) => {
    const value = e.target.value;
    setProductSearch(value);
    setSelectedProduct(null);
    
    if (value.length > 0) {
      const filtered = products.filter(p => 
        p.item_name.toLowerCase().includes(value.toLowerCase()) ||
        p.category.toLowerCase().includes(value.toLowerCase()) ||
        (p.vendor_name && p.vendor_name.toLowerCase().includes(value.toLowerCase()))
      );
      setFilteredProducts(filtered.slice(0, 15));
      setShowProductDropdown(true);
    } else {
      setFilteredProducts([]);
      setShowProductDropdown(false);
    }
  };

  // Handle vendor search
  const handleVendorSearch = (e) => {
    const value = e.target.value;
    setVendorSearch(value);
    setSelectedVendor(null);
    
    if (value.length > 0) {
      const filtered = vendors.filter(v => 
        v.name.toLowerCase().includes(value.toLowerCase()) ||
        (v.phone && v.phone.includes(value)) ||
        (v.category && v.category.toLowerCase().includes(value.toLowerCase()))
      );
      setFilteredVendors(filtered.slice(0, 15));
      setShowVendorDropdown(true);
    } else {
      setFilteredVendors([]);
      setShowVendorDropdown(false);
    }
  };

  // Click outside handlers
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (productDropdownRef.current && !productDropdownRef.current.contains(event.target) &&
          productInputRef.current && !productInputRef.current.contains(event.target)) {
        setShowProductDropdown(false);
      }
      if (vendorDropdownRef.current && !vendorDropdownRef.current.contains(event.target) &&
          vendorInputRef.current && !vendorInputRef.current.contains(event.target)) {
        setShowVendorDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="purchase-page">
      <div className="purchase-header">
        <h1>Purchase Management</h1>
        <div className="purchase-header-actions">
          {isAdmin && (
            <>
              <button onClick={() => {
                setShowProductList(true);
                fetchProducts();
              }} className="vendor-list-btn">
                Manage Products ({productList.length})
              </button>
              <button onClick={() => {
                setShowVendorList(true);
                fetchVendors();
              }} className="vendor-list-btn">
                Manage Vendors ({vendorList.length})
              </button>
            </>
          )}
          <button onClick={() => setShowForm(true)} className="add-purchase-btn">
            New Purchase
          </button>
        </div>
      </div>

      {message.text && (
        <div className={`purchase-toast ${message.type}`}>
          {message.text}
        </div>
      )}

      {/* Purchase Form Modal */}
      {showForm && (
        <div className="purchase-modal" onClick={() => setShowForm(false)}>
          <div className="purchase-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="purchase-modal-title">
              <h2>{editingId ? "Edit Purchase" : "New Purchase"}</h2>
              <button onClick={() => setShowForm(false)}>×</button>
            </div>
            
            <div className="purchase-form">
              {/* Product Selection */}
              <div className="purchase-form-row">
                <div className="purchase-field full-width">
                  <label>Search Product *</label>
                  <div className="product-search">
                    <input
                      ref={productInputRef}
                      type="text"
                      placeholder="Type to search products..."
                      value={productSearch}
                      onChange={handleProductSearch}
                      autoComplete="off"
                    />
                    {showProductDropdown && filteredProducts.length > 0 && (
                      <div className="product-dropdown" ref={productDropdownRef}>
                        {filteredProducts.map(p => (
                          <div key={p.id} className="product-option" onClick={() => selectProduct(p)}>
                            <div className="product-name">{p.item_name}</div>
                            <div className="product-details">
                              <span className="category">{p.category}</span>
                              <span className="unit">{p.base_unit}</span>
                              {p.purchase_packaging && <span>{p.packaging_quantity} {p.purchase_packaging}</span>}
                            </div>
                          </div>
                        ))}
                        {isAdmin && filteredProducts.length < products.length && (
                          <div className="product-option add-new" onClick={() => {
                            setShowProductDropdown(false);
                            setShowProductModal(true);
                          }}>
                            + Add New Product
                          </div>
                        )}
                      </div>
                    )}
                    {showProductDropdown && filteredProducts.length === 0 && productSearch && (
                      <div className="product-dropdown">
                        <div className="product-option no-results">
                          No products found. 
                          {isAdmin && <button onClick={() => {
                            setShowProductDropdown(false);
                            setShowProductModal(true);
                          }}> Add New?</button>}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Vendor Selection */}
              <div className="purchase-form-row">
                <div className="purchase-field full-width">
                  <label>Search Vendor *</label>
                  <div className="vendor-search">
                    <input
                      ref={vendorInputRef}
                      type="text"
                      placeholder="Type to search vendors..."
                      value={vendorSearch}
                      onChange={handleVendorSearch}
                      autoComplete="off"
                    />
                    {showVendorDropdown && filteredVendors.length > 0 && (
                      <div className="vendor-dropdown" ref={vendorDropdownRef}>
                        {filteredVendors.map(v => (
                          <div key={v.id} className="vendor-option" onClick={() => selectVendor(v)}>
                            <div className="vendor-name">{v.name}</div>
                            <div className="vendor-details">
                              {v.phone && <span>Phone: {v.phone}</span>}
                              {v.category && <span>Category: {v.category}</span>}
                            </div>
                          </div>
                        ))}
                        {isAdmin && filteredVendors.length < vendors.length && (
                          <div className="vendor-option add-new" onClick={() => {
                            setShowVendorDropdown(false);
                            setShowVendorModal(true);
                          }}>
                            + Add New Vendor
                          </div>
                        )}
                      </div>
                    )}
                    {showVendorDropdown && filteredVendors.length === 0 && vendorSearch && (
                      <div className="vendor-dropdown">
                        <div className="vendor-option no-results">
                          No vendors found.
                          {isAdmin && <button onClick={() => {
                            setShowVendorDropdown(false);
                            setShowVendorModal(true);
                          }}> Add New?</button>}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Product Info Display - NO recommended price */}
              {selectedProduct && (
                <div className="product-info-card">
                  <div className="info-title">Product Information</div>
                  <div className="info-grid">
                    <div><label>Category:</label> {selectedProduct.category}</div>
                    <div><label>Base Unit:</label> {selectedProduct.base_unit}</div>
                    <div><label>Packaging:</label> {selectedProduct.purchase_packaging || 'None'}</div>
                    <div><label>Units/Pack:</label> {selectedProduct.packaging_quantity || 1}</div>
                  </div>
                </div>
              )}

              {/* Purchase Details */}
              <div className="purchase-form-row">
                <div className="purchase-field">
                  <label>Number of Packs *</label>
                  <input
                    type="number"
                    placeholder="How many packs?"
                    value={formData.quantity}
                    onChange={handleQuantityChange}
                  />
                </div>
                <div className="purchase-field">
                  <label>Packaging Type</label>
                  <input
                    type="text"
                    value={formData.packaging_type || (selectedProduct?.purchase_packaging || "Unit")}
                    disabled
                    className="readonly-field"
                  />
                </div>
              </div>

              <div className="purchase-form-row">
                <div className="purchase-field">
                  <label>Price per Pack (TZS) *</label>
                  <input
                    type="number"
                    placeholder="Total price for this pack"
                    value={formData.price_per_pack}
                    onChange={handlePackPriceChange}
                  />
                </div>
                <div className="purchase-field">
                  <label>Total Amount (TZS)</label>
                  <input
                    type="text"
                    value={safeNumber(formData.total_amount) ? safeToLocaleString(formData.total_amount) : ""}
                    disabled
                    className="total-field"
                  />
                </div>
              </div>

              <div className="purchase-form-row">
                <div className="purchase-field">
                  <label>Price per Unit (TZS)</label>
                  <input
                    type="text"
                    value={safeNumber(formData.price_per_unit) ? safeToFixed(formData.price_per_unit, 2) : ""}
                    disabled
                    className="unit-price-field"
                  />
                  <small>
                    {formData.price_per_pack && formData.quantity && formData.packaging_quantity ? 
                      `${safeToLocaleString(formData.price_per_pack)} / (${safeNumber(formData.quantity)} × ${safeNumber(formData.packaging_quantity)} units) = ${safeToFixed(formData.price_per_pack / (safeNumber(formData.quantity) * safeNumber(formData.packaging_quantity)), 2)} TZS/unit` : 
                      'Enter pack price and quantity to calculate unit price'}
                  </small>
                </div>
              </div>
            </div>

            <div className="purchase-modal-actions">
              <button onClick={resetForm} className="cancel-btn">Cancel</button>
              <button onClick={savePurchase} disabled={loading} className="save-btn">
                {loading ? "Saving..." : editingId ? "Update" : "Save Purchase"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Product Management Modal - NO recommended price */}
      {showProductModal && (
        <div className="purchase-modal" onClick={() => resetProductForm()}>
          <div className="purchase-modal-card product-modal" onClick={(e) => e.stopPropagation()}>
            <div className="purchase-modal-title">
              <h2>{editingProduct ? "Edit Product" : "New Product"}</h2>
              <button onClick={() => resetProductForm()}>×</button>
            </div>
            
            <div className="purchase-form">
              <div className="purchase-form-row">
                <div className="purchase-field">
                  <label>Category *</label>
                  <select value={productForm.category} onChange={(e) => setProductForm({...productForm, category: e.target.value})}>
                    <option value="">Select Category</option>
                    {productCategories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
                <div className="purchase-field">
                  <label>Item Name *</label>
                  <input type="text" placeholder="Product name" value={productForm.item_name} onChange={(e) => setProductForm({...productForm, item_name: e.target.value})} />
                </div>
              </div>
              
              <div className="purchase-form-row">
                <div className="purchase-field">
                  <label>Base Unit *</label>
                  <select value={productForm.base_unit} onChange={(e) => setProductForm({...productForm, base_unit: e.target.value})}>
                    <option value="PCS">PCS (Pieces)</option>
                    <option value="KG">KG (Kilograms)</option>
                    <option value="GR">GR (Grams)</option>
                    <option value="LTR">LTR (Liters)</option>
                    <option value="ML">ML (Milliliters)</option>
                  </select>
                </div>
                <div className="purchase-field">
                  <label>Purchase Packaging</label>
                  <select value={productForm.purchase_packaging} onChange={(e) => setProductForm({...productForm, purchase_packaging: e.target.value})}>
                    <option value="">None (sold as base unit)</option>
                    <option value="CARTON">Carton</option>
                    <option value="BOX">Box</option>
                    <option value="PACK">Pack</option>
                    <option value="BOTTLE">Bottle</option>
                    <option value="TRAY">Tray</option>
                    <option value="BAG">Bag</option>
                  </select>
                </div>
              </div>
              
              <div className="purchase-form-row">
                <div className="purchase-field">
                  <label>Units per Pack</label>
                  <input type="number" placeholder="How many units in one pack?" value={productForm.packaging_quantity} onChange={(e) => setProductForm({...productForm, packaging_quantity: e.target.value})} />
                  <small>If buying in packs, how many pieces/units per pack?</small>
                </div>
              </div>
              
              <div className="purchase-form-row">
                <div className="purchase-field full-width">
                  <label>Preferred Vendor</label>
                  <select value={productForm.vendor_id} onChange={(e) => {
                    const vendor = vendorList.find(v => v.id == e.target.value);
                    setProductForm({
                      ...productForm,
                      vendor_id: vendor?.id || "",
                      vendor_name: vendor?.name || ""
                    });
                  }}>
                    <option value="">Select Vendor</option>
                    {vendorList.map(v => (
                      <option key={v.id} value={v.id}>{v.name}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            
            <div className="purchase-modal-actions">
              <button onClick={() => resetProductForm()} className="cancel-btn">Cancel</button>
              <button onClick={saveProduct} disabled={savingItem} className="save-btn">
                {savingItem ? "Saving..." : editingProduct ? "Update Product" : "Create Product"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Product List Modal - NO recommended price */}
      {showProductList && (
        <div className="purchase-modal" onClick={() => setShowProductList(false)}>
          <div className="purchase-modal-card product-list-modal" onClick={(e) => e.stopPropagation()}>
            <div className="purchase-modal-title">
              <h2>Products ({productList.length})</h2>
              <button onClick={() => setShowProductList(false)}>×</button>
            </div>
            
            <div className="product-list-header">
              <input
                type="text"
                placeholder="Search products..."
                className="list-search"
                onChange={(e) => {
                  const search = e.target.value;
                  if (search) {
                    const filtered = productList.filter(p => 
                      p.item_name.toLowerCase().includes(search.toLowerCase()) ||
                      p.category.toLowerCase().includes(search.toLowerCase())
                    );
                    setFilteredProducts(filtered);
                  } else {
                    setFilteredProducts(productList);
                  }
                }}
              />
              <button onClick={() => {
                setShowProductList(false);
                setShowProductModal(true);
                setEditingProduct(null);
              }} className="add-item-btn">
                New Product
              </button>
            </div>
            
            <div className="product-list">
              {(filteredProducts.length > 0 ? filteredProducts : productList).map(product => (
                <div key={product.id} className="product-list-item">
                  <div className="product-info">
                    <h4>{product.item_name}</h4>
                    <div className="product-meta">
                      <span className="category-badge">{product.category}</span>
                      <span className="unit-badge">{product.base_unit}</span>
                      {product.purchase_packaging && <span className="packaging-badge">{product.packaging_quantity} {product.purchase_packaging}</span>}
                    </div>
                  </div>
                  <div className="product-actions">
                    <button onClick={() => editProduct(product)} className="edit-item">Edit</button>
                    <button onClick={() => deleteProduct(product.id)} className="delete-item">Delete</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Vendor List Modal - unchanged */}
      {showVendorList && (
        <div className="purchase-modal" onClick={() => setShowVendorList(false)}>
          <div className="purchase-modal-card vendor-list-modal" onClick={(e) => e.stopPropagation()}>
            <div className="purchase-modal-title">
              <h2>Vendors ({vendorList.length})</h2>
              <button onClick={() => setShowVendorList(false)}>×</button>
            </div>
            
            <div className="vendor-list-header">
              <input
                type="text"
                placeholder="Search vendors..."
                className="list-search"
                onChange={(e) => {
                  const search = e.target.value;
                  if (search) {
                    const filtered = vendorList.filter(v => 
                      v.name.toLowerCase().includes(search.toLowerCase()) ||
                      (v.phone && v.phone.includes(search))
                    );
                    setFilteredVendors(filtered);
                  } else {
                    setFilteredVendors(vendorList);
                  }
                }}
              />
              <button onClick={() => {
                setShowVendorList(false);
                setShowVendorModal(true);
                setEditingVendor(null);
              }} className="add-item-btn">
                New Vendor
              </button>
            </div>
            
            <div className="vendor-list">
              {(filteredVendors.length > 0 ? filteredVendors : vendorList).map(vendor => (
                <div key={vendor.id} className="vendor-list-item">
                  <div className="vendor-info">
                    <h4>{vendor.name}</h4>
                    <div className="vendor-meta">
                      {vendor.phone && <span>Phone: {vendor.phone}</span>}
                      {vendor.payment_methods && <span>Payment: {vendor.payment_methods}</span>}
                      {vendor.delivery_type && <span>Delivery: {vendor.delivery_type}</span>}
                      {vendor.category && <span>Category: {vendor.category}</span>}
                    </div>
                  </div>
                  <div className="vendor-actions">
                    <button onClick={() => editVendor(vendor)} className="edit-item">Edit</button>
                    <button onClick={() => deleteVendor(vendor.id)} className="delete-item">Delete</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Vendor Management Modal - unchanged */}
      {showVendorModal && (
        <div className="purchase-modal" onClick={() => resetVendorForm()}>
          <div className="purchase-modal-card vendor-modal" onClick={(e) => e.stopPropagation()}>
            <div className="purchase-modal-title">
              <h2>{editingVendor ? "Edit Vendor" : "New Vendor"}</h2>
              <button onClick={() => resetVendorForm()}>×</button>
            </div>
            
            <div className="purchase-form">
              <div className="purchase-form-row">
                <div className="purchase-field full-width">
                  <label>Vendor Name *</label>
                  <input type="text" placeholder="Vendor name" value={vendorForm.name} onChange={(e) => setVendorForm({...vendorForm, name: e.target.value})} />
                </div>
              </div>
              
              <div className="purchase-form-row">
                <div className="purchase-field">
                  <label>Phone</label>
                  <input type="text" placeholder="Phone number" value={vendorForm.phone} onChange={(e) => setVendorForm({...vendorForm, phone: e.target.value})} />
                </div>
                <div className="purchase-field">
                  <label>Payment Methods</label>
                  <input type="text" placeholder="CASH, MOBILE, CARD" value={vendorForm.payment_methods} onChange={(e) => setVendorForm({...vendorForm, payment_methods: e.target.value})} />
                </div>
              </div>
              
              <div className="purchase-form-row">
                <div className="purchase-field">
                  <label>Delivery Type</label>
                  <input type="text" placeholder="by delivery, pickup" value={vendorForm.delivery_type} onChange={(e) => setVendorForm({...vendorForm, delivery_type: e.target.value})} />
                </div>
                <div className="purchase-field">
                  <label>ID Number</label>
                  <input type="text" placeholder="TIN / ID" value={vendorForm.id_number} onChange={(e) => setVendorForm({...vendorForm, id_number: e.target.value})} />
                </div>
              </div>
              
              <div className="purchase-form-row">
                <div className="purchase-field full-width">
                  <label>Category</label>
                  <select value={vendorForm.category} onChange={(e) => setVendorForm({...vendorForm, category: e.target.value})}>
                    <option value="">Select Category</option>
                    {vendorCategories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            
            <div className="purchase-modal-actions">
              <button onClick={() => resetVendorForm()} className="cancel-btn">Cancel</button>
              <button onClick={saveVendor} disabled={savingItem} className="save-btn">
                {savingItem ? "Saving..." : editingVendor ? "Update Vendor" : "Create Vendor"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Filter Bar */}
      <div className="purchase-filters">
        <div className="filter-group">
          <label>Filter by date</label>
          <input type="date" value={filterDate} onChange={(e) => setFilterDate(e.target.value)} />
        </div>
        <button onClick={() => window.open(`https://okna.co.tz/mrms/Purchase.php?export=1&date=${filterDate}&token=${getToken()}`, "_blank")} className="export-btn">
          Export Report
        </button>
      </div>

      {/* Purchases Table */}
      <div className="purchases-table-wrapper">
        {loading ? (
          <div className="loading-state">Loading purchases...</div>
        ) : purchases.length === 0 ? (
          <div className="empty-state">No purchases found</div>
        ) : (
          <table className="purchases-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Date</th>
                <th>Product</th>
                <th>Category</th>
                <th>Vendor</th>
                <th>Packs</th>
                <th>Packaging</th>
                <th>Pack Price</th>
                <th>Unit Price</th>
                <th>Total</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {purchases.map((p, idx) => (
                <tr key={p.id}>
                  <td>{idx + 1}</td>
                  <td>{new Date(p.created_at).toLocaleDateString()}</td>
                  <td><strong>{p.product_name || p.item_name}</strong></td>
                  <td>{p.category}</td>
                  <td>{p.vendor_name || p.vendor || "-"}</td>
                  <td>{safeToLocaleString(p.quantity)}</td>
                  <td>{p.packaging_type || p.unit}</td>
                  <td>TZS {safeToLocaleString(p.price_per_pack || p.price)}</td>
                  <td>TZS {safeToLocaleString(p.price_per_unit)}</td>
                  <td className="total-amount">TZS {safeToLocaleString(p.total_amount || p.amount)}</td>
                  <td className="actions">
                    <button onClick={() => editPurchase(p)} className="edit-row" title="Edit">Edit</button>
                    <button onClick={() => deletePurchase(p.id)} className="delete-row" title="Delete">Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="table-footer">
                <td colSpan="8"><strong>Grand Total:</strong></td>
                <td colSpan="3">
                  <strong>TZS {safeToLocaleString(purchases.reduce((sum, p) => sum + safeNumber(p.total_amount || p.amount), 0))}</strong>
                </td>
              </tr>
            </tfoot>
          </table>
        )}
      </div>
    </div>
  );
}

export default Purchase;